import { Box, Button } from '@deckee/deck-hand';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import { checkClientToken } from '../../../../services/backend';
import { captureEvent } from '../../../../services/event-capture';
import { get } from '../../../../services/local-storage';
import { showPageHolder } from '../../../../services/store/modules/app-controls';
import { requestValidationOfClientTokenViaEmailToken } from '../../../../services/user';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import ErrorMessage from '../../../shared/ErrorMessage/ErrorMessage';
import { ReactSvg, deckeeLogo } from '../../../shared/vector-images';
import SignInForm from '../components/SignInForm';
import VerifyEmail from '../components/VerifyEmail';

const SignInPage: FC = () => {
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<{ message: string } | null>(null);
  const [emailInterval, setEmailInterval] = useState<ReturnType<
    typeof setInterval
  > | null>(null);

  const focusInputRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const requestEmailAuthentication = async () => {
    setSubmitting(true);
    try {
      await requestValidationOfClientTokenViaEmailToken(email);
      const token = (await get('backendService', 'clientToken')) || '';

      // Obsfuscate token UUID with the '#' symbols before sending event to Amplitude
      const tokenChunks = token.split('-');
      const obfuscatedToken = tokenChunks
        .map((chunk: any, index: number) =>
          index < tokenChunks.length - 1 ? '####' : chunk,
        )
        .join('-');

      captureEvent({
        type: 'signup: submit email',
        payload: {
          token: obfuscatedToken,
          email,
        },
      });
      setSubmitted(true);
      if (emailInterval) clearInterval(emailInterval);
      setEmailInterval(
        setInterval(async () => {
          await checkClientToken();
        }, 2000),
      );
    } catch (err) {
      setError({
        message: 'There was an issue logging in',
      });
    }

    setSubmitting(false);
  };

  useEffect(() => {
    dispatch(showPageHolder(true));
    checkClientToken();

    // Focus the input
    setTimeout(() => {
      if (focusInputRef && focusInputRef.current) {
        focusInputRef.current.focus();
      }
    }, 500);

    return () => {
      if (emailInterval) clearInterval(emailInterval);
    };
  }, [dispatch, emailInterval, focusInputRef]);

  const handleRetry = () => {
    captureEvent({
      type: 'signup: retry email',
      payload: {
        email,
      },
    });
    setSubmitted(false);
  };

  return (
    <BasicDrawerLayout
      showHeaderBar={false}
      onClose={null}
      showMenuBar={false}
      showMapOverlay
    >
      <Box marginTop="4">
        <ReactSvg
          toDisplay={deckeeLogo()}
          options={{
            fillColor: theme.colors.primary,
          }}
          height={70}
        />
      </Box>
      <Box display="flex" flexDirection="column" height="100%" padding="23px">
        <Box fontSize="4" fontWeight="bold">
          START EXPLORING
          <br />
          ON THE WATER
          <br />
          WITH
          <Box display="inline" color="primary">
            &nbsp;CONFIDENCE&nbsp;
          </Box>
        </Box>

        {!error && !submitted && (
          <SignInForm
            requestEmailAuthentication={requestEmailAuthentication}
            updateField={setEmail}
            submitting={submitting}
            email={email}
            focusInputRef={focusInputRef}
          />
        )}
        {!submitting && submitted && (
          <VerifyEmail email={email} retry={handleRetry} />
        )}

        {error && (
          <Box p="3">
            <ErrorMessage description="There was a problem communicating with the server. Please ensure that you're online, check your email is valid and try again" />
            <br />
            <Button
              type="secondary"
              size="medium"
              action={() => setError(null)}
            >
              Try again
            </Button>
          </Box>
        )}
      </Box>
    </BasicDrawerLayout>
  );
};

export default SignInPage;
